import { createElement, useEffect, useState } from 'react';
import {
  consentWrite,
  scribeActionDecide,
  scribeDiscard,
  scribeDraft,
  scribeResidents,
  scribeSession,
  scribeSessions,
  scribeSign,
  scribeStart,
  scribeTranscript,
  scribeTypes,
  type ApiError,
  type ScribeNoteType,
  type ScribeResidentsResponse,
  type ScribeSession,
  type ScribeSessionsResponse,
  type ScribeTypesResponse,
} from '@aithora/shared';
import { useSession } from '../../session/SessionContext';
import { facilitiesParam, usePickedIds } from '../../session/FacilityScope';
import { ScopeEmpty, nothingFrom } from '../../ui/ScopeEmpty';
import { useQuery } from '../../useQuery';
import { useReadOnlyReason } from '../../ui/ReadOnly';
import { sx } from '../../ui/designStyles';
import { ErrorNote } from '../../ErrorNote';
import { ConfirmDialog } from '../../ui/ConfirmDialog';
import { stop, useNow, type AtlasRole } from './common';
import { ACTION_ROUTE, BADGE_DRAFTED, BADGE_OFF, BADGE_SIGNED, BADGE_WAITING, SC_GREEN, SC_RED, sessionRow, typeName } from './scribeCommon';

const INK = '#22272E';
const MUTE = '#54606C';
const BLUE = '#2F5C97';
const HEAD = 'font-size:14px;font-weight:700;letter-spacing:.09em;text-transform:uppercase;color:#54606C;';
const ORDER: ScribeNoteType[] = ['resident_note', 'shift_handoff', 'worklist_note', 'incident'];

type Phase = 'idle' | 'live' | 'review' | 'signed';

/**
 * The design's "What you can say" (scribeVals say), spoken about the resident picked here
 * (first name, room) rather than the design's sample residents; generic words when none is.
 */
export function sayFor(first: string | null, room: string | null) {
  return [
    { g: 'Runs right away', items: [
      [first ? `“Hey Siri, start an Atlas note for ${first}”` : '“Hey Siri, start an Atlas note”', 'Opens this tab and starts listening'],
      ['“Hey Siri, ask Atlas who’s due for meds”', 'Reads the Worklist aloud'],
      [first ? `“Hey Google, open ${first}’s chart in Atlas”` : '“Hey Google, open a resident’s chart in Atlas”', 'Opens the resident page'],
    ] },
    { g: 'Drafted, you sign', items: [
      ['“Mark the 2 pm BP check done”', 'Closes the task after you sign'],
      [first ? `“Snooze ${first}’s snack an hour”` : '“Snooze a snack an hour”', 'Moves the task, logged'],
      [room ? `“Report a fall in room ${room}”` : '“Report a fall”', 'Drafts the Guardian incident'],
    ] },
  ];
}

const mmss = (sec: number) => Math.floor(sec / 60) + ':' + String(Math.max(0, Math.floor(sec % 60))).padStart(2, '0');

/**
 * Scribe (design: atIsScribe, T13253; scribeVals L31864): the device's own speech engine
 * hears, Atlas drafts only what was said, and a person signs. Staff write their own notes;
 * a Facility Admin also co-signs; Aithora previews every building's sessions.
 */
export function ScribeTab({ ar, scopeName }: { ar: AtlasRole; scopeName: string | null }) {
  const { client, session: me } = useSession();
  const now = useNow();
  const roReason = useReadOnlyReason();
  const preview = ar === 'Operator';
  const writeReason = roReason ?? (preview ? 'Operator preview: staff run the scribe, so nothing starts from here.' : null);

  const types = useQuery<ScribeTypesResponse>((signal) => scribeTypes(client, { signal }), [client]);
  const residents = useQuery<ScribeResidentsResponse>((signal) => scribeResidents(client, { signal }), [client]);
  // The facility switcher (V6): the team's preview lists the picked buildings' notes.
  const pickIds = usePickedIds();
  const facilities = preview ? facilitiesParam(pickIds) : undefined;
  const sessions = useQuery<ScribeSessionsResponse>((signal) => scribeSessions(client, { query: facilities ? { facilities } : undefined, signal }), [client, facilities]);

  const [type, setType] = useState<ScribeNoteType>('resident_note');
  const [resIdx, setResIdx] = useState(0);
  const [active, setActive] = useState<ScribeSession | null>(null);
  const [phase, setPhase] = useState<Phase>('idle');
  const [draftTxt, setDraftTxt] = useState('');
  const [error, setError] = useState<ApiError | Error | null>(null);
  const [consentBlocked, setConsentBlocked] = useState(false);
  const [askConsent, setAskConsent] = useState(false);

  const live = phase === 'live';
  const isHand = type === 'shift_handoff';
  const R0 = residents.data?.residents[resIdx] ?? null;
  const R = isHand ? { id: '', name: active?.subjectLabel ?? 'Shift handoff', consent: true, room: null, unit: null } : R0;
  const consent = isHand || (!!R0 && R0.consent && !consentBlocked);

  // While listening, the transcript arrives from the device's assistant: read it back.
  useEffect(() => {
    if (!live || !active) return;
    const id = window.setInterval(() => {
      scribeSession(client, { params: { id: active.id } })
        .then((r) => setActive(r.session))
        .catch(() => undefined);
    }, 2000);
    return () => window.clearInterval(id);
  }, [live, active?.id, client]); // eslint-disable-line react-hooks/exhaustive-deps

  const run = async (f: () => Promise<void>) => {
    setError(null);
    try {
      await f();
    } catch (e) {
      const err = e as ApiError;
      if (err?.status === 409 && (err.body as { reason?: string } | null)?.reason === 'no_recording_consent') setConsentBlocked(true);
      else setError(err);
    }
  };
  const start = () =>
    run(async () => {
      if (!consent || writeReason) return;
      const r = await scribeStart(client, { body: { noteType: type, ...(isHand ? {} : { residentId: R0?.id, ...(type === 'worklist_note' && R0?.taskRef ? { taskRef: R0.taskRef } : {}) }) } });
      setActive(r.session);
      setPhase('live');
    });
  const end = () =>
    run(async () => {
      if (!active) return;
      setPhase('review');
      const r = await scribeDraft(client, { params: { id: active.id } });
      setActive(r.session);
    });
  const reset = () =>
    run(async () => {
      if (active) await scribeDiscard(client, { params: { id: active.id } });
      setActive(null);
      setPhase('idle');
    });
  const addTyped = () =>
    run(async () => {
      const v = draftTxt.trim();
      if (!v || !active) return;
      const r = await scribeTranscript(client, { params: { id: active.id }, body: { lines: [v], speaker: 'typed' } });
      setActive(r.session);
      setDraftTxt('');
    });
  const decide = (actionId: string, decision: 'keep' | 'drop') =>
    run(async () => {
      if (!active) return;
      const r = await scribeActionDecide(client, { params: { id: active.id, actionId }, body: { decision } });
      setActive(r.session);
    });
  const sign = () =>
    run(async () => {
      if (!active) return;
      let s = active;
      // Anything not dropped is kept: signing is the decision.
      for (const a of s.actions.filter((x) => x.decision === null)) {
        s = (await scribeActionDecide(client, { params: { id: s.id, actionId: a.id }, body: { decision: 'keep' } })).session;
      }
      const r = await scribeSign(client, { params: { id: s.id } });
      setActive(r.session);
      setPhase('signed');
      sessions.reload();
    });

  const allTypes = [...(types.data?.types ?? [])].sort((a, b) => ORDER.indexOf(a.type) - ORDER.indexOf(b.type));
  const def = allTypes.find((t) => t.type === type) ?? null;
  const audioKept = !!types.data?.audio.accepted;
  const sess = (sessions.data?.sessions ?? []).map((s) => sessionRow(s, now));

  const heardLines = (active?.transcript ?? []).filter((l) => l.speaker !== 'typed');
  const typedLines = (active?.transcript ?? []).filter((l) => l.speaker === 'typed');
  const fieldsBase = active?.fields.length ? active.fields : (def?.fields ?? []).map((f) => ({ key: f.key, label: f.label, status: 'NOT_HEARD' as const, value: null, lines: [] as number[], source: null }));
  const filled = fieldsBase.filter((f) => f.status !== 'NOT_HEARD' && f.value).length;
  const acts = active?.actions ?? [];
  const keptN = acts.filter((a) => a.decision !== 'drop').length;
  const elapsed = active && (live || phase === 'review') ? Math.max(0, (now - Date.parse(active.createdAt)) / 1000) : 0;
  const firstName = (R?.name ?? '').split(' ')[0];
  // Why Start listening cannot start now: read only or the preview, else no resident picked or
  // no recording consent (the consent line below says how to record it).
  const startReason = writeReason ?? (isHand ? null : !R0 ? 'Pick who it’s about first.' : !consent ? `${R0.name} has no recording consent on file.` : null);
  const orgName = scopeName ?? me?.user.orgName ?? 'Your building';

  const pulse = createElement('span', { style: { width: 9, height: 9, borderRadius: 5, flex: '0 0 auto', background: live ? SC_RED : phase === 'signed' ? SC_GREEN : '#9AA3AD', ...(live ? { animation: 'pRed 1.4s ease-out infinite' } : {}) } });
  const vu = live
    ? createElement(
        'span',
        { style: { display: 'inline-flex', alignItems: 'center', gap: 2, height: 20 } },
        [0, 1, 2, 3, 4, 5, 6].map((i) => createElement('i', { key: i, style: { display: 'block', width: 3, borderRadius: 2, background: SC_RED, height: 7, animation: 'vu ' + (0.7 + (i % 3) * 0.18) + 's ease-in-out ' + i * 0.09 + 's infinite' } })),
      )
    : null;

  // A pick with no notes in it (design geoLayer, Atlas).
  if (facilities && sessions.data && !sessions.data.sessions.length) {
    return <ScopeEmpty title={nothingFrom} body="No notes from the buildings in this view yet. Staff write them with the scribe on the floor." />;
  }

  return (
    <div data-testid="atlas-scribe" style={sx('flex:1 1 auto;min-height:0;min-width:0;display:flex;flex-direction:column;gap:10px;')}>
      <div style={sx('flex:0 0 auto;display:flex;flex-wrap:wrap;align-items:center;gap:10px 14px;min-width:0;padding-bottom:10px;border-bottom:1px solid rgba(34,39,46,.14);')}>
        <div style={sx('display:flex;align-items:center;gap:8px;min-width:0;')}>
          {pulse}
          <span data-testid="scribe-status" style={sx('font-size:17px;font-weight:700;letter-spacing:-.02em;white-space:nowrap;' + (live ? 'color:' + SC_RED + ';' : phase === 'signed' ? 'color:' + SC_GREEN + ';' : 'color:' + INK + ';'))}>
            {live ? 'Listening · ' + mmss(elapsed) : phase === 'review' ? 'Stopped · review and sign' : phase === 'signed' ? 'Signed and filed' : 'Ready'}
          </span>
          {vu}
        </div>
        <div style={sx('display:flex;flex-wrap:wrap;gap:4px;min-width:0;')}>
          {allTypes.map((t) => (
            <a
              key={t.type}
              href="#"
              aria-pressed={t.type === type}
              onClick={(e) => {
                stop(e);
                if (live) return;
                setType(t.type);
                setPhase('idle');
                setActive(null);
              }}
              style={sx(
                'display:inline-flex;align-items:center;height:30px;padding:0 12px;border-radius:7px;font-size:16px;font-weight:600;text-decoration:none;white-space:nowrap;' +
                  (t.type === type ? 'color:#FCFAF4;background:' + BLUE + ';' : 'color:#3C454F;background:rgba(34,39,46,.05);') +
                  (live && t.type !== type ? 'opacity:.45;cursor:default;' : ''),
              )}
            >
              {typeName(t.type)}
            </a>
          ))}
        </div>
        <div style={sx('margin-left:auto;display:flex;align-items:center;gap:8px;')}>
          {/* Always drawn while idle (V7), held back with its reason when it cannot start. */}
          {(phase === 'idle' || phase === 'signed') && (
            <a
              href="#"
              data-testid="scribe-start"
              aria-disabled={!!startReason || undefined}
              title={startReason ?? undefined}
              onClick={(e) => {
                stop(e);
                void start();
              }}
              style={sx('display:inline-flex;align-items:center;gap:8px;height:40px;padding:0 18px;border-radius:9px;font-size:16px;font-weight:700;text-decoration:none;white-space:nowrap;color:#FCFAF4;background:#2F5C97;' + (startReason ? 'opacity:.55;cursor:not-allowed;' : ''))}
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <rect x="9" y="3" width="6" height="12" rx="3" />
                <path d="M5 11a7 7 0 0 0 14 0M12 18v3" />
              </svg>
              Start listening
            </a>
          )}
          {live && (
            <a href="#" onClick={(e) => { stop(e); void end(); }} style={sx('display:inline-flex;align-items:center;height:40px;padding:0 18px;border-radius:9px;font-size:16px;font-weight:700;text-decoration:none;white-space:nowrap;color:#FCFAF4;background:#B23A3A;')}>
              Stop
            </a>
          )}
          {phase === 'review' && (
            <a href="#" onClick={(e) => { stop(e); void reset(); }} style={sx('display:inline-flex;align-items:center;height:40px;padding:0 14px;border-radius:9px;font-size:16px;font-weight:600;text-decoration:none;white-space:nowrap;color:#3C454F;background:rgba(34,39,46,.06);')}>
              Discard
            </a>
          )}
        </div>
        <div style={sx('flex:1 1 100%;display:flex;flex-wrap:wrap;align-items:center;gap:6px 14px;min-width:0;')}>
          <span style={sx('font-size:16px;color:#3C454F;min-width:0;text-wrap:pretty;')}>
            {'Or say “Hey Siri, ' + (isHand ? 'start an Atlas shift handoff' : firstName ? 'start an Atlas note for ' + firstName : 'start an Atlas note') + '”. Android: “Hey Google”. Windows and Mac: the mic key.'}
          </span>
          <span style={sx('font-size:14px;color:#54606C;min-width:0;')}>
            {ar === 'Care Staff'
              ? 'Your residents, this shift. You sign your own notes.'
              : ar === 'Facility Admin'
                ? orgName + '. You can sign your own notes and co-sign staff notes.'
                : 'Operator preview. Staff run the scribe; you see every facility’s sessions.'}
          </span>
        </div>
      </div>

      {error && <ErrorNote error={error as ApiError} compact />}

      <div style={sx('flex:1 1 auto;min-height:0;min-width:0;display:grid;grid-template-columns:minmax(0,1.05fr) minmax(0,1fr) minmax(0,.95fr);gap:22px;')}>
        <div style={sx('min-width:0;min-height:0;display:flex;flex-direction:column;overflow:hidden;')}>
          {!isHand && (
            <>
              <div style={sx(HEAD + 'padding-bottom:6px;')}>Who it’s about</div>
              <div data-testid="scribe-residents" style={sx('flex:1 1 auto;min-height:0;overflow-y:auto;align-content:start;display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:6px;min-width:0;padding-bottom:4px;')}>
                {residents.data && residents.data.residents.length === 0 && (
                  <div style={sx('grid-column:1 / -1;font-size:16px;line-height:1.45;color:#54606C;')}>
                    Care Staff and Facility Admins pick a resident here. This account previews the scribe and never writes a note, so no residents are listed.
                  </div>
                )}
                {(residents.data?.residents ?? []).map((r, i) => (
                  <a
                    key={r.id}
                    href="#"
                    aria-pressed={i === resIdx}
                    onClick={(e) => {
                      stop(e);
                      if (live) return;
                      setResIdx(i);
                      setPhase('idle');
                      setActive(null);
                      setConsentBlocked(false);
                    }}
                    style={sx(
                      'display:flex;flex-direction:column;min-width:0;padding:6px 10px;border-radius:7px;text-decoration:none;border:1px solid ' +
                        (i === resIdx ? 'rgba(47,92,151,.45)' : 'rgba(34,39,46,.12)') +
                        ';background:' +
                        (i === resIdx ? 'rgba(47,92,151,.09)' : 'transparent') +
                        ';',
                    )}
                  >
                    <span style={sx('font-size:16px;font-weight:700;color:#22272E;overflow-wrap:anywhere;')}>{r.name}</span>
                    <span style={sx('font-size:14px;line-height:1.35;' + (r.consent ? 'color:' + MUTE + ';' : 'color:' + SC_RED + ';'))}>
                      {[r.room ? 'Rm ' + r.room : null, r.unit].filter(Boolean).join(' · ') + (r.consent ? '' : ' · no recording consent')}
                    </span>
                  </a>
                ))}
              </div>
            </>
          )}
          {!consent && R && (
            <div data-testid="scribe-consent" style={sx('display:flex;flex-wrap:wrap;align-items:center;gap:8px 12px;min-width:0;margin-top:10px;padding:10px 0;border-top:1px solid rgba(178,58,58,.3);border-bottom:1px solid rgba(178,58,58,.3);')}>
              <span style={sx('flex:1 1 220px;font-size:16px;color:#8C2B2B;line-height:1.45;text-wrap:pretty;')}>
                {R.name + ' has no recording consent on file, so the scribe will not start. Record it here once they (or their representative) have agreed.'}
              </span>
              <a href="#" aria-disabled={!!writeReason || undefined} title={writeReason ?? undefined} onClick={(e) => { stop(e); if (!writeReason) setAskConsent(true); }} style={sx('flex:0 0 auto;display:inline-flex;align-items:center;height:32px;padding:0 12px;border-radius:7px;font-size:16px;font-weight:700;text-decoration:none;white-space:nowrap;color:#FCFAF4;background:#2F5C97;')}>
                Record consent
              </a>
            </div>
          )}
          {askConsent && R && me?.user.organizationId && (
            <ConfirmDialog
              title={`Record that ${R.name} agreed to be recorded?`}
              body="The scribe may then listen while you work with them. It goes on their consent record under your name, and they can withdraw it at any time."
              confirmLabel="Record consent"
              onConfirm={async () => {
                await consentWrite(client, { body: { userId: R.id, organizationId: me.user.organizationId!, consentType: 'scribe_recording', granted: true } });
                setConsentBlocked(false);
                residents.reload();
              }}
              onClose={() => setAskConsent(false)}
            />
          )}
          {(live || phase === 'review') && (
            <>
              <div style={sx(HEAD + 'padding:12px 0 2px;')}>Live transcript</div>
              <div style={sx('flex:0 1 auto;min-height:0;overflow-y:auto;min-width:0;')}>
                {[...heardLines, ...typedLines].map((l, i) => {
                  const who = l.speaker === 'typed' ? 'Typed' : l.speaker === 'resident' ? 'Resident' : l.speaker === 'staff' ? 'Staff' : '';
                  return (
                    <div key={l.n} style={sx('display:flex;gap:8px;min-width:0;padding:7px 0;border-top:1px solid rgba(34,39,46,.09);animation:fadeIn .35s ease-out, riseIn .35s ease-out;')}>
                      <span style={sx('flex:0 0 auto;width:66px;font-size:14px;font-weight:700;letter-spacing:.06em;text-transform:uppercase;padding-top:2px;color:' + (who === 'Staff' ? BLUE : who === 'Typed' ? '#1F6B52' : '#6B4E8C') + ';')}>{who}</span>
                      <span style={sx('flex:1 1 auto;min-width:0;font-size:16px;color:#22272E;line-height:1.45;text-wrap:pretty;')}>{l.text}</span>
                      <span style={sx('flex:0 0 auto;font-size:14px;color:#54606C;font-variant-numeric:tabular-nums;padding-top:3px;')}>{String(i + 1)}</span>
                    </div>
                  );
                })}
              </div>
              <div style={sx('flex:0 0 auto;display:flex;gap:6px;min-width:0;margin-top:8px;')}>
                <input
                  aria-label="Add to the note"
                  value={draftTxt}
                  onChange={(e) => setDraftTxt(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      void addTyped();
                    }
                  }}
                  placeholder="Type to add to the note, or correct what Atlas heard"
                  style={sx('flex:1 1 auto;min-width:0;height:38px;padding:0 12px;border-radius:8px;border:1px solid rgba(34,39,46,.2);background:#FCFAF4;font:inherit;font-size:16px;color:#22272E;outline:none;')}
                />
                <a href="#" onClick={(e) => { stop(e); void addTyped(); }} style={sx('flex:0 0 auto;display:inline-flex;align-items:center;height:38px;padding:0 14px;border-radius:8px;font-size:16px;font-weight:700;text-decoration:none;color:#FCFAF4;background:#2F5C97;')}>
                  Add
                </a>
              </div>
            </>
          )}
          <div style={sx(HEAD + 'padding:14px 0 2px;')}>Today’s notes</div>
          <div style={sx('flex:1 1 auto;min-height:0;overflow-y:auto;min-width:0;')}>
            {sess.map((x) => (
              <div key={x.id} style={sx('display:flex;flex-direction:column;gap:3px;min-width:0;padding:8px 0;border-top:1px solid rgba(34,39,46,.09);')}>
                <div style={sx('display:flex;align-items:center;gap:8px;min-width:0;')}>
                  <span style={sx('font-size:16px;font-weight:700;color:#22272E;min-width:0;overflow-wrap:break-word;')}>
                    <span>{x.who}</span> · <span>{x.type}</span>
                  </span>
                  <span style={sx('margin-left:auto;' + x.stStyle)}>{x.state}</span>
                </div>
                <div style={sx('font-size:14px;color:#54606C;')}>
                  <span>{x.when}</span> · <span>{x.note}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div style={sx('min-width:0;min-height:0;display:flex;flex-direction:column;overflow:hidden;')}>
          <div style={sx('display:flex;align-items:baseline;flex-wrap:wrap;gap:2px 8px;min-width:0;padding-bottom:6px;')}>
            <span style={sx(HEAD + 'white-space:nowrap;')}>
              Draft · <span>{typeName(type)}</span>
            </span>
            <span style={sx('margin-left:auto;min-width:0;font-size:14px;color:#54606C;text-align:right;text-wrap:pretty;')}>{filled + ' of ' + fieldsBase.length + ' filled from what was said'}</span>
          </div>
          <div style={sx('font-size:16px;font-weight:700;color:#22272E;letter-spacing:-.01em;padding-bottom:4px;')}>{R?.name ?? ''}</div>
          <div style={sx('flex:0 1 auto;min-height:0;overflow-y:auto;min-width:0;')}>
            {fieldsBase.map((f) => {
              const heard = f.status !== 'NOT_HEARD' && !!f.value;
              return (
                <div key={f.key} style={sx('display:grid;grid-template-columns:96px minmax(0,1fr) auto;align-items:baseline;gap:4px 10px;min-width:0;padding:7px 0;border-top:1px solid rgba(34,39,46,.09);')}>
                  <span style={sx('font-size:14px;font-weight:700;color:#3C454F;')}>{f.label}</span>
                  <span style={sx('min-width:0;font-size:16px;line-height:1.45;text-wrap:pretty;' + (heard ? 'color:' + INK + ';font-weight:600;' : 'color:#54606C;font-style:italic;'))}>{heard ? f.value : 'Not heard · left blank'}</span>
                  <span style={sx('display:flex;align-items:center;gap:5px;justify-self:end;')}>
                    <span style={sx('font-size:14px;color:#54606C;white-space:nowrap;')}>{heard && f.lines.length ? 'line ' + f.lines[0] : ''}</span>
                    <span style={sx(heard ? (phase === 'signed' || f.status === 'SIGNED' ? BADGE_SIGNED : BADGE_DRAFTED) : BADGE_OFF)}>{heard ? (phase === 'signed' || f.status === 'SIGNED' ? 'SIGNED' : 'DRAFTED') : 'NOT HEARD'}</span>
                  </span>
                </div>
              );
            })}
            {typedLines.length > 0 && (
              <div style={sx('display:grid;grid-template-columns:96px minmax(0,1fr) auto;align-items:baseline;gap:4px 10px;min-width:0;padding:7px 0;border-top:1px solid rgba(34,39,46,.09);')}>
                <span style={sx('font-size:14px;font-weight:700;color:#3C454F;')}>Added by you</span>
                <span style={sx('min-width:0;font-size:16px;line-height:1.45;text-wrap:pretty;color:#22272E;font-weight:600;')}>{typedLines.map((l) => l.text).join(' ')}</span>
                <span style={sx('display:flex;align-items:center;gap:5px;justify-self:end;')}>
                  <span style={sx('font-size:14px;color:#54606C;white-space:nowrap;')}>typed</span>
                  <span style={sx(BADGE_SIGNED)}>{phase === 'signed' ? 'SIGNED' : 'TYPED'}</span>
                </span>
              </div>
            )}
          </div>
          <div style={sx('flex:0 0 auto;min-width:0;padding-top:12px;border-top:1px solid rgba(34,39,46,.14);')}>
            <div style={sx(HEAD + 'padding-bottom:4px;')}>When you sign</div>
            {[
              ['Record', (isHand ? 'The signed handoff goes to the evening shift lead' : 'The signed note goes on ' + (R?.name ?? 'the resident') + '’s chart') + ', marked as drafted by Atlas and signed by you.'],
              ['Worklist', 'Tasks and hand-offs appear on the right shift, with you as the source.'],
              ['Messages', 'Nurse and family messages send after your signature, never before.'],
              ['Truth Ledger', audioKept ? 'The note, every action and the audio deletion are sealed permanently.' : 'The note and every action are sealed permanently.'],
            ].map(([k, v]) => (
              <div key={k} style={sx('display:flex;align-items:baseline;gap:10px;min-width:0;padding:5px 0;border-top:1px solid rgba(34,39,46,.08);')}>
                <span style={sx('flex:0 0 104px;font-size:14px;font-weight:700;color:#2F5C97;')}>{k}</span>
                <span style={sx('flex:1 1 auto;min-width:0;font-size:16px;color:#3C454F;line-height:1.4;text-wrap:pretty;')}>{v}</span>
              </div>
            ))}
            <div style={sx('font-size:14px;color:#54606C;line-height:1.5;padding-top:8px;text-wrap:pretty;')}>
              {audioKept
                ? 'Audio is kept until you sign, or ' + (types.data?.audio.maxDays ?? 14) + ' days at most, then deleted. The transcript, signed note and every action stay on the Truth Ledger. Audio never trains a model.'
                : 'No audio is kept: only the transcript is. The transcript, signed note and every action stay on the Truth Ledger. Nothing you say trains a model.'}
            </div>
          </div>
        </div>

        <div style={sx('min-width:0;min-height:0;display:flex;flex-direction:column;overflow:hidden;')}>
          <div style={sx(HEAD + 'padding-bottom:2px;')}>Actions Atlas heard</div>
          <div style={sx('font-size:14px;color:#54606C;padding-bottom:4px;')}>{live ? 'Atlas lists actions as it hears them. Nothing runs until you sign.' : 'Nothing runs until you sign.'}</div>
          <div style={sx('flex:0 1 auto;min-height:0;overflow-y:auto;min-width:0;')}>
            {acts.length === 0 && (
              <div style={sx('font-size:16px;color:#54606C;padding:9px 0;border-top:1px solid rgba(34,39,46,.1);font-style:italic;')}>None yet. Tasks, messages and reports show up here as they’re said.</div>
            )}
            {acts.map((a) => {
              const dropped = a.decision === 'drop';
              const signed = phase === 'signed';
              return (
                <div key={a.id} style={sx('display:flex;flex-direction:column;gap:3px;min-width:0;padding:9px 0;border-top:1px solid rgba(34,39,46,.1);animation:fadeIn .35s ease-out;' + (dropped ? 'opacity:.55;' : ''))}>
                  <div style={sx('display:flex;align-items:flex-start;gap:8px;min-width:0;')}>
                    <span style={sx('flex:1 1 auto;min-width:0;font-size:16px;font-weight:700;color:' + INK + ';line-height:1.35;text-wrap:pretty;' + (dropped ? 'text-decoration:line-through;' : ''))}>{a.text}</span>
                    {!signed && !dropped && (
                      <a href="#" onClick={(e) => { stop(e); void decide(a.id, 'drop'); }} style={sx('flex:0 0 auto;display:inline-flex;align-items:center;height:26px;padding:0 10px;border-radius:6px;font-size:14px;font-weight:700;text-decoration:none;color:#3C454F;background:rgba(34,39,46,.07);')}>
                        Drop
                      </a>
                    )}
                    {!signed && dropped && (
                      <a href="#" onClick={(e) => { stop(e); void decide(a.id, 'keep'); }} style={sx('flex:0 0 auto;display:inline-flex;align-items:center;height:26px;padding:0 10px;border-radius:6px;font-size:14px;font-weight:700;text-decoration:none;color:#2F5C97;background:rgba(47,92,151,.09);')}>
                        Keep
                      </a>
                    )}
                  </div>
                  <div style={sx('display:flex;flex-wrap:wrap;align-items:center;gap:4px 8px;min-width:0;')}>
                    <span style={sx(dropped ? BADGE_OFF : signed ? BADGE_SIGNED : BADGE_WAITING)}>{dropped ? 'DROPPED' : signed ? 'SEALED' : 'WAITS FOR SIGNATURE'}</span>
                    <span style={sx('font-size:14px;color:#54606C;')}>{(a.routeLabel ?? ACTION_ROUTE[a.kind] ?? a.kind) + (a.lines.length ? ' · from line ' + a.lines[0] : '')}</span>
                  </div>
                </div>
              );
            })}
          </div>
          {phase === 'review' && (
            <a
              href="#"
              aria-disabled={!!writeReason || undefined}
              title={writeReason ?? undefined}
              onClick={(e) => {
                stop(e);
                if (!writeReason) void sign();
              }}
              style={sx('flex:0 0 auto;display:flex;align-items:center;justify-content:center;height:44px;margin-top:10px;border-radius:9px;font-size:16px;font-weight:700;text-decoration:none;color:#FCFAF4;background:#1F6B52;')}
            >
              {'Sign and file · ' + keptN + ' actions'}
            </a>
          )}
          {phase === 'signed' && (
            <div style={sx('flex:0 0 auto;font-size:16px;color:#12503C;line-height:1.45;margin-top:10px;padding:9px 0;border-top:1px solid rgba(31,107,82,.3);text-wrap:pretty;')}>
              {'Filed to ' + (R?.name ?? 'the') + '’s record. ' + keptN + ' actions sealed to the Truth Ledger.' + (audioKept ? ' The audio is deleted now that it’s signed.' : '')}
            </div>
          )}
          <div style={sx('flex:1 1 auto;min-height:0;overflow-y:auto;min-width:0;margin-top:12px;')}>
            <div style={sx(HEAD + 'padding-bottom:2px;')}>What you can say</div>
            {sayFor(isHand ? null : R0 ? R0.name.split(' ')[0] : null, isHand ? null : R0?.room ?? null).map((g) => (
              <div key={g.g} style={sx('min-width:0;padding-top:6px;')}>
                <div style={sx('font-size:14px;font-weight:700;color:#2F5C97;padding-bottom:2px;')}>{g.g}</div>
                {g.items.map(([p, w]) => (
                  <div key={p} style={sx('display:flex;flex-direction:column;min-width:0;padding:4px 0;border-top:1px solid rgba(34,39,46,.08);')}>
                    <span style={sx('font-size:16px;font-weight:600;color:#22272E;line-height:1.35;')}>{p}</span>
                    <span style={sx('font-size:14px;color:#54606C;')}>{w}</span>
                  </div>
                ))}
              </div>
            ))}
            <div style={sx('font-size:14px;color:#54606C;line-height:1.5;padding-top:8px;text-wrap:pretty;')}>
              Mic not working? It’s your device’s setting. iPhone and iPad: Settings › Aithora › Microphone. Android: Settings › Apps › Aithora › Permissions. Mac and Windows: the browser’s site settings.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
